import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
// Gold geometric diamond pattern shared with the Footer's decorative band
// (see `Footer.tsx`). Used here as the decorative "background" tile that
// sits behind the square slideshow in the right column — it bleeds past
// the bottom-right corner of the composition so the foreground square
// reads as deliberately placed on top of a patterned backdrop, echoing
// the same diamond motif visitors see at the very bottom of every page.
import footerPatternUrl from '../assets/footer-pattern.png';

// ============================================================================
// Slot Group / Collection Definitions
// ----------------------------------------------------------------------------
// The right column shows a single large square image that auto-cycles
// through the property's `photo_gallery` as a mini-slideshow. Instead of
// the previous pair of back/front photo slot groups (one image each),
// this binds the whole collection so all gallery images flow through
// the square automatically — no per-slot editing required. The diamond
// pattern stays as chrome (a CSS background tile, not an editable slot)
// so brand consistency with the footer stays locked in.
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Browse Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Browse Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

// Auto-advance interval for the slideshow. 5s reads as "deliberately
// pacing through curated imagery" rather than a hectic carousel — long
// enough for the eye to settle on each photo, short enough that a
// visitor sitting on the section sees the slideshow IS a slideshow
// without having to wait.
const SLIDE_INTERVAL_MS = 5000;

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const photos = useCollectionBinding<GalleryPhoto>('propertyoverview.gallery', {
    collection: 'photo_gallery',
    defaultLimit: 8,
  });

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const imagesRef = useScrollReveal<HTMLDivElement>();

  const total = photos.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // Normalize the (possibly negative / overflowing) raw index into the
  // valid range so prev/next can be unbounded increments — simpler than
  // wrapping at every callsite.
  const safeIndex = total > 0 ? ((activeIndex % total) + total) % total : 0;

  useEffect(() => {
    if (total <= 1 || paused) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => i + 1);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [total, paused]);

  const goPrev = () => setActiveIndex((i) => i - 1);
  const goNext = () => setActiveIndex((i) => i + 1);

  return (
    <section
      // Overflow strategy: clip on the X axis, visible on the Y. The
      // diamond pattern in the right column is positioned with
      // `right: -14%` so it deliberately bleeds past the right edge of
      // its image-composition container — with a plain
      // `overflow-visible` section that bleed would escape all the way
      // out to the document edge and trigger a horizontal scrollbar on
      // viewports narrower than the bleed reaches. `overflow-x: clip`
      // (Tailwind's `overflow-x-clip`) traps the horizontal bleed at
      // the section's right edge without establishing a scroll
      // container and — unlike `hidden` — it does NOT force
      // `overflow-y` to `auto`. Any future ancestor that adds
      // `overflow: hidden` would clip the pattern's downward bleed
      // too, so flagging the intent here keeps the layout resilient
      // under refactors.
      className="pt-12 md:pt-16 lg:pt-20 pb-0 relative overflow-x-clip"
      // Static neutral gray — same `#f3f4f6` the header's upper utility row
      // uses so the section reads as a calm, chrome-adjacent band that
      // bookends the photographic hero above it. Intentionally NOT
      // `var(--vibe-color-background)` so the surface stays consistent
      // across brand palettes (some properties run a tinted background
      // token that would mute this layout).
      style={{ backgroundColor: '#f3f4f6' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Asymmetric two-column grid on lg+: left column (6fr) vs
            right (7fr) — roughly 46/54 — so the copy stack
            (pre-title / heading / blurb / CTA) has enough horizontal
            runway for the description to extend further right before
            wrapping, while the right column still skews larger so
            the photo composition reads as the visual centerpiece.
            (Originally a tighter 5fr / 8fr; widened to give the
            description room to breathe at its current `text-base`
            size.) Mobile collapses to a single full-width stack via
            `grid-cols-1`. */}
        <div className="grid grid-cols-1 lg:grid-cols-[6fr_7fr] gap-12 lg:gap-16 items-center">
          {/* ---- Left column: pre-title, title, blurb, CTA ----
              `self-start` overrides the grid's `items-center` for just
              this column so the copy stack (pre-title / title / blurb /
              CTA) anchors to the TOP of the row rather than centering
              against the taller image composition on the right. */}
          <div ref={headingRef} className="reveal-fade-up self-start lg:mt-4">
            <RichTextContent
              path="propertyoverview.pre_title"
              as="p"
              className="font-body text-xs sm:text-sm text-[var(--vibe-color-secondary)] mt-2 mb-5 tracking-[0.2em] uppercase font-semibold"
            />

            {/* Title color is locked to `--vibe-color-primary` so it
                anchors the section with the primary brand color as the
                "headline accent." Sized down a step from the hero's
                4xl→7xl scale so the section reads as a supporting beat
                rather than a second hero, then nudged DOWN one more
                step (xl→2xl→3xl) and locked to `font-bold` so the
                heading reads tighter and weightier — a compact, bold
                section opener rather than a softer display heading. */}
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="font-heading font-bold text-xl sm:text-2xl md:text-3xl mb-8 leading-tight"
              style={{ color: 'var(--vibe-color-primary)' }}
            />

            {/* Blurb sized at `text-base` (1rem) — a step up from the
                previous `text-sm` so the body copy carries more
                presence next to the smaller secondary-colored heading
                without competing with it. The earlier `max-w-xl`
                (576px) cap has been removed so the description can
                run to the natural right edge of its containing
                column, giving the larger type more horizontal runway
                to breathe before wrapping.

                Indent is mobile-clean (`ml-0` implicit) so the copy
                hugs the column's left edge — flush with the eyebrow
                and title above — and only picks up the `ml-16` tab
                at md+ where it intentionally pairs with the CTA
                indent rhythm. Without this scoping, mobile readers
                would see the description hanging 3rem to the right
                of the heading, which reads as a misaligned column
                rather than a deliberate indent. */}
            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="font-body text-base text-[var(--vibe-color-text)] leading-[1.92] [&_a]:text-primary [&_a]:underline mb-10 md:ml-16"
            />

            {/* CTA — exact treatment lifted from the Header's Schedule a
                Tour pill (`.header-nav-cta` in `Header.tsx`):
                  - secondary fill, text-on-secondary label
                  - no border (borderless pill)
                  - 2px radius, uppercase label, weight 600, 0.05em
                    tracking, 0.875rem font-size, 1rem / 2.5rem padding
                  - hover swaps the fill to primary + flips the label to
                    text-on-primary, and lifts the button 1px
                Ports the values verbatim (rather than reusing the
                `.header-nav-cta` class) so this component stays self-
                contained — if the header's CTA selector ever changes
                under it, the overview button doesn't silently break. */}
            <EditableSlotGroup
              groupPath="propertyoverview.cta_link"
              definition={ctaLinkDef}
              className="overview-cta-wrap"
            >
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta md:ml-16"
              >
                {ctaLink.label || 'Browse Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* ---- Right column: square slideshow on a diamond-pattern backdrop ----
              Layer order (back → front):
                1. Diamond gold pattern tile (CSS background-image) —
                   tucked into the bottom-right corner of the wrapper
                   so it peeks out from behind the lower-right of the
                   photo. Uses the SAME asset the Footer renders so the
                   motif feels deliberately repeated rather than
                   coincidental.
                2. Single square slideshow — cycles through
                   `photo_gallery` with crossfade. Sized to occupy most
                   of the composition so it dominates while still
                   letting the pattern peek from behind.
              The wrapper is `aspect-square` so the slideshow and its
              backdrop scale together as a single composition across
              viewport widths without JS measurement. Pattern positions
              are all in `%` of the wrapper, so the L-shape backdrop
              keeps its corner-wrap geometry at every viewport width. */}
          <div
            ref={imagesRef}
            className="reveal-fade-up relative w-full aspect-square max-w-[640px] md:max-w-[520px] lg:max-w-[640px] mx-auto lg:ml-auto lg:-mr-8"
          >
            {/* Layer 1 — diamond pattern (decorative backdrop) */}
            <div
              className="overview-pattern absolute"
              aria-hidden="true"
              role="presentation"
            />

            {/* Layer 2 — square slideshow.
                Positioned at top-left of the wrapper at 88%
                width/height so the diamond pattern peeks out from the
                bottom-right corner as a decorative backdrop. The slide
                container itself is square (88% × 88% of an already-
                square parent), so each photo crops to a clean square
                regardless of the source aspect ratio.

                Auto-advance pauses while the user hovers (desktop) or
                focuses inside the slideshow (keyboard) — so a visitor
                inspecting a specific image isn't yanked off it. Pause
                is also implicit via the prev/next buttons, which
                directly drive `activeIndex`. */}
            <div
              className="overview-slideshow"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              role="region"
              aria-roledescription="carousel"
              aria-label="Property gallery slideshow"
            >
              {total === 0 ? (
                <div className="overview-slideshow__empty">
                  Gallery photos will appear here.
                </div>
              ) : (
                <>
                  {photos.map((photo, i) => {
                    const src = photo.image ?? photo.original_url ?? '';
                    const alt = photo.alt_text ?? photo.title ?? 'Community photo';
                    const isActive = i === safeIndex;
                    return (
                      <div
                        key={`${src || 'slide'}-${i}`}
                        className={`overview-slideshow__slide${isActive ? ' is-active' : ''}`}
                        aria-hidden={isActive ? 'false' : 'true'}
                      >
                        {src ? (
                          <VibesitesImage
                            url={src}
                            alt={alt}
                            className="overview-slideshow__img"
                          />
                        ) : null}
                      </div>
                    );
                  })}

                  {total > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={goPrev}
                        className="overview-slideshow__arrow overview-slideshow__arrow--prev"
                        aria-label="Previous image"
                      >
                        &#8249;
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        className="overview-slideshow__arrow overview-slideshow__arrow--next"
                        aria-label="Next image"
                      >
                        &#8250;
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ============================================================
           Diamond pattern decoration — sits BEHIND the square
           slideshow and is sized so it forms an L-shape wrapping just
           the bottom-right corner of the image:

             - a SHORT horizontal band below the image, NOT spanning
               its full width (anchored well inward from the left edge)
             - a narrower vertical band along the image's right edge,
               extending up only to about the image's vertical midpoint

           The pattern element is a rectangle tucked into the lower-
           right region of the wrapper (\`left: 7%; right: 57px;
           top: 18%; bottom: 0\`). The left edge starts ~7% inward
           from the wrapper so the visible bottom band of the L-shape
           extends well left under the image but stops just shy of
           the image's left edge. The RIGHT edge sits \`57px\` inside
           the wrapper, which places the pattern's right edge exactly
           \`20px\` past the image's right edge (image is 88% of a 640px
           wrapper at lg = 563px wide; pattern right at \`640 - 57 = 583px\`
           = 20px past image right). That \`20px\` sliver is the only
           bit of pattern visible to the right of the image — a tight
           accent rather than a wide bleed.

           The TOP edge at \`18%\` places the right band's top edge
           about 80% of the way up the image's right side (image top
           at 0%, image bottom at 88%, so the right band runs from
           \`18%\` up to \`88%\` = 70% of wrapper height = ~80% of image
           height). The bottom edge stays flush with the wrapper's
           bottom so the bottom band sits in the small gap below the
           image.

           Tile scale is set to 65% so each diamond reads as a
           single oversized graphic element — about 1.5 tiles fit
           across the pattern rectangle, so the visible L-shape shows
           one bold diamond per dimension rather than a repeating
           tile field. The pattern is the SAME PNG the Footer
           renders (see \`Footer.tsx\`'s \`.footer-pattern-row\`) so the
           motif reads as a deliberate brand repeat rather than a
           one-off.
           ============================================================ */
        .overview-pattern {
          left: 7%;
          right: 57px;
          top: 18%;
          bottom: 0;
          background-image: url(${footerPatternUrl});
          background-repeat: repeat;
          background-size: 65%;
          background-position: 100% 100%;
        }

        /* ============================================================
           Landscape slideshow — single large 4:3 image (88% × 88% of
           the parent 4:3 wrapper) anchored to the top-left of the
           composition so the diamond pattern peeks out behind the
           bottom-right corner. All slides are absolutely stacked at
           inset:0 and crossfaded via opacity, with the active slide on
           top of the inactive ones.
           ============================================================ */
        .overview-slideshow {
          position: absolute;
          top: 0;
          left: 0;
          width: 88%;
          height: 88%;
          overflow: hidden;
          background-color: #1f2937;
        }

        /* Mobile: the asymmetric L-shape composition (slideshow top-left,
           pattern peeking bottom-right) reads as visually off-center
           when the stage is the only thing in its row. Hide the
           decorative pattern and let the slideshow fill the centered
           square stage so the gallery sits squarely centered in the
           column. The md+ asymmetric composition is untouched. */
        @media (max-width: 767.98px) {
          .overview-pattern { display: none; }
          .overview-slideshow {
            width: 100%;
            height: 100%;
          }
        }

        .overview-slideshow__empty {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.7);
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          text-align: center;
          padding: 1rem;
        }

        .overview-slideshow__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.9s ease-in-out;
          pointer-events: none;
        }

        .overview-slideshow__slide.is-active {
          opacity: 1;
          pointer-events: auto;
        }

        .overview-slideshow__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* Prev/Next arrow buttons — bare white chevrons (no background
           pill) so the slideshow chrome stays minimal and lets the
           photo behind it read cleanly. A text-shadow keeps the
           chevrons legible against light areas of the underlying
           image. Edge-anchored at 50% Y so they sit centered against
           the square. */
        .overview-slideshow__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          color: #fff;
          font-size: 2.5rem;
          line-height: 1;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0.15rem 0.55rem;
          opacity: 0.9;
          text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
          z-index: 2;
          -webkit-user-select: none;
          user-select: none;
          transition: opacity 0.15s, transform 0.15s;
        }
        .overview-slideshow__arrow:hover {
          opacity: 1;
        }
        .overview-slideshow__arrow:focus-visible {
          opacity: 1;
          outline: 2px solid var(--vibe-color-accent, #fff);
          outline-offset: 2px;
        }
        .overview-slideshow__arrow--prev { left: 0.5rem; }
        .overview-slideshow__arrow--next { right: 0.5rem; }

        @media (prefers-reduced-motion: reduce) {
          .overview-slideshow__slide {
            transition: none;
          }
        }

        /* ============================================================
           CTA — exact port of \`.header-nav-cta\` from \`Header.tsx\`,
           so the overview's "Browse Our Floor Plans" pill matches the
           header's "Schedule a Tour" pill character-for-character on
           fill, radius, hover swap, and lift:
             - secondary fill, text-on-secondary label
             - no border (borderless pill)
             - 2px radius, uppercase label, weight 600, 0.05em tracking,
               0.875rem font-size, 1rem / 2.5rem padding
             - hover → primary fill / text-on-primary, translateY(-1px)
             - active → settles back to the resting position
           All values match \`.header-nav-cta\` verbatim so the two main
           filled pills on the home page read as a matched pair.
           ============================================================ */
        /* Wrapper around the CTA pill — block-level so it can govern
           horizontal alignment of the inline pill inside. On mobile
           the pill centers; at md+ it hugs the left edge so the
           link's own \`md:ml-16\` indent rhythm with the description
           kicks back in. */
        .overview-cta-wrap {
          display: block;
          text-align: center;
        }
        @media (min-width: 768px) {
          .overview-cta-wrap {
            text-align: left;
          }
        }

        .overview-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 2.5rem;
          background-color: var(--vibe-color-secondary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;
        }
        .overview-cta:hover {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
        }
        .overview-cta:active {
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Pre-title',
      type: 'richtext',
      defaultValue:
        '<span data-type="field-token" data-field="property_name"></span> | Welcome Home',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Experience life in <span data-type="field-token" data-field="property_address_city"></span></p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        '<p>Look no further for a place that feels like home. Our spacious apartments are designed for quality, comfort, and convenience. Our centrally-located apartments are conveniently downtown, with the beach right outside your doorstep. Choose from a variety of floor plans to fit your lifestyle.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'propertyoverview.gallery': {
      label: 'Slideshow Photos',
      collection: 'photo_gallery',
      defaultLimit: 8,
      description:
        'Photos that cycle through the large square slideshow on the right side of the section. Pulled from the property photo gallery.',
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
